import { useEffect, useRef } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';

/**
 * Custom hook to optimize bandwidth usage based on network conditions
 * Monitors network quality and logs information for debugging
 * LiveKit's adaptiveStream handles most of the optimization automatically
 */
export const useBandwidthOptimization = () => {
  const roomContext = useRoomContext();
  const room = roomContext?.room;
  const localParticipant = useLocalParticipant();
  const previousQualityRef = useRef(null);

  useEffect(() => {
    if (!room || !localParticipant?.localParticipant) return;

    const optimizeForBandwidth = () => {
      // Check network connection quality
      let connection = null;
      if ('connection' in navigator) {
        connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      }

      // Determine network quality for logging
      let networkQuality = 'unknown';
      
      if (connection) {
        const { effectiveType, downlink, saveData } = connection;
        
        if (saveData) {
          networkQuality = 'low (save data mode)';
        } else if (effectiveType) {
          networkQuality = effectiveType;
        } else if (downlink) {
          if (downlink < 1) {
            networkQuality = 'low';
          } else if (downlink < 2.5) {
            networkQuality = 'medium';
          } else {
            networkQuality = 'high';
          }
        }

        // Log network quality changes
        if (previousQualityRef.current !== networkQuality) {
          console.log('Network quality changed:', previousQualityRef.current, '->', networkQuality);
          previousQualityRef.current = networkQuality;
        }
      }

      // LiveKit's adaptiveStream and dynacast options handle quality adjustment automatically
      // We just monitor and log the state here
    };

    // Initial check
    optimizeForBandwidth();

    // Monitor network changes
    let connection = null;
    if ('connection' in navigator) {
      connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        connection.addEventListener('change', optimizeForBandwidth);
      }
    }

    // Monitor connection quality from LiveKit
    const handleConnectionQualityUpdate = (update) => {
      console.log('Connection quality update:', update);
      optimizeForBandwidth();
    };

    if (room.localParticipant) {
      room.localParticipant.on('connectionQualityChanged', handleConnectionQualityUpdate);
    }

    return () => {
      if (connection) {
        connection.removeEventListener('change', optimizeForBandwidth);
      }
      if (room.localParticipant) {
        room.localParticipant.off('connectionQualityChanged', handleConnectionQualityUpdate);
      }
    };
  }, [room, localParticipant]);
};

